import React, { useState, useRef, useEffect } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Dropzone from "react-dropzone";
import { BsFileEarmarkArrowUp } from "react-icons/bs";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import styles from "./CustomizInput.module.css";

function Inputs({
  ref,
  label,
  optionalLable,
  type,
  name,
  id,
  onInputChange, // ✅ Add this prop
  value = "",
  onChange,
  error,
  showErrorMessage = true,
  placeholder,
  disabled,
  options = [],
  radioOptions = [],
  showWordCount = false,
  isMultiSelect = false,
  allowedTypes,
  maxFileSize,
  maxLength,
}) {
  const inputType = (type || "").toLowerCase();
  const [showPassword, setShowPassword] = useState(() => false);

  const charCount =
    (inputType === "textarea" || inputType === "text area") &&
    typeof value === "string"
      ? value.length
      : 0;

  const disabledStyles = {
    opacity: 0.5,
    cursor: "not-allowed",
  };
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused
        ? "2px solid #fa8316"
        : "2px solid hsl(0, 0%, 90%)", // light gray default
      boxShadow: "none",
      "&:hover": {
        border: state.isFocused
          ? "2px solid #fa8316"
          : "2px solid hsl(0, 0%, 90%)",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#142454"
        : state.isFocused
          ? "#142454" // apply when hovered/focused
          : "white",
      color: state.isSelected ? "white" : state.isFocused ? "white" : "#142454",
      // force override default hover
      "&:hover": {
        backgroundColor: "#142454 !important",
        color: "white !important",
      },
    }),
  };

  const inputRef = useRef(null);

  const handleTextChange = (e) => {
    const { name } = e.target;
    let newValue = e.target.value;

    // For textarea, preserve newlines and don't clean them
    if (inputType === "textarea" || inputType === "text area") {
      // Only remove emojis, preserve everything else including newlines
      let cleanedValue = newValue.replace(
        /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{2700}-\u{27BF}]/gu,
        "",
      );

      // Remove invisible emoji variant selectors
      cleanedValue = cleanedValue.replace(/[\uFE0E\uFE0F]/g, "");

      let finalValue = cleanedValue;

      // Enforce maxLength (if provided)
      if (maxLength && finalValue.length > maxLength) {
        finalValue = finalValue.slice(0, maxLength);
      }

      // Trigger React onChange immediately
      onChange({ target: { name, value: finalValue } });
      return;
    }
    // Skip cleanup for URL-like fields
    const urlLikeFields = ["link", "url", "website", "location", "source"];

    if (urlLikeFields.some((field) => name.toLowerCase().includes(field))) {
      onChange({ target: { name, value: newValue } });
      return;
    }

    let cleanedValue = newValue
      .replace(
        /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{2700}-\u{27BF}]/gu,
        "",
      )
      .replace(/[\uFE0E\uFE0F]/g, "");

    let finalValue = cleanedValue
      .replace(/^[.,?\s]+/, "")
      .replace(/\s{2,}/g, " ")
      .replace(/([.,?])\1+/g, "$1");

    onChange({ target: { name, value: finalValue } });

    // ✅ Enforce maxLength (if provided)
    if (maxLength && finalValue.length > maxLength) {
      finalValue = finalValue.slice(0, maxLength);
    }

    // ✅ Trigger React onChange immediately
    onChange({ target: { name, value: finalValue } });

    // ✅ Force revalidation if emoji paste left field empty
    if (finalValue.trim() === "") {
      setTimeout(() => {
        onChange({ target: { name, value: "" } });
      }, 50);
    }
  };

  return (
    <>
      <Form.Group>
        {label && inputType !== "checkbox" && (
          <Form.Label
            className={`${
              disabled
                ? `${styles["disabled-label"]} body-highlight`
                : "body-highlight"
            } ${styles.pointer}`}
          >
            {label}
            <span
              className={
                disabled ? styles["disabled-label"] : "body-text italicStyle"
              }
            >
              {" "}
              {optionalLable ? optionalLable : ""}
            </span>
          </Form.Label>
        )}

        {inputType === "select" && (
          <Select
            name={name}
            id={id}
            styles={customStyles}
            isMulti={isMultiSelect}
            closeMenuOnSelect={!isMultiSelect}
            hideSelectedOptions={false}
            ref={inputRef}
            options={options}
            value={value} // ✅ pass the object (or array of objects for multi)
            onChange={(selected) => onChange(selected)} // ✅ pass back the object(s)
            onInputChange={onInputChange} // ✅ Pass onInputChange to Select
            placeholder={placeholder || "Select an option"}
            isDisabled={disabled}
            isClearable
            classNamePrefix="react-select body-text"
          />
        )}

        {inputType === "creatable-select" && (
          <CreatableSelect
            isClearable
            isMulti={isMultiSelect} // ✅ Enables multi-select
            name={name}
            id={id}
            styles={customStyles}
            options={options}
            placeholder={placeholder || "Select or create option"}
            isDisabled={disabled}
            classNamePrefix="react-select"
            value={
              isMultiSelect
                ? value // Should be an array of { label, value }
                : value && typeof value === "object"
                  ? value
                  : options.find((opt) => opt.value === value) || null
            }
            closeMenuOnSelect={!isMultiSelect}
            getNewOptionData={(inputValue, optionLabel) => ({
              label: optionLabel,
              value: optionLabel, // set value same as label
            })}
            // 🧹 FILTER EMOJIS IN THE INPUT BOX
            onInputChange={(inputValue, actionMeta) => {
              if (["input-change", "input-blur"].includes(actionMeta.action)) {
                const cleanedInput = inputValue.replace(
                  /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{2700}-\u{27BF}]|[\uFE0E\uFE0F]/gu,
                  "",
                );
                return cleanedInput; // ✅ blocks emoji typing
              }
              return inputValue;
            }}
            onChange={(selected, actionMeta) => {
              if (isMultiSelect) {
                if (actionMeta.action === "create-option") {
                  // Handle newly created option
                  const newOption = selected[selected.length - 1];
                  onChange([...value, { ...newOption, value: "" }]);
                } else if (actionMeta.action === "remove-value") {
                  // Handle removed value
                  const removed = actionMeta.removedValue;

                  const updated = value.filter(
                    (item) =>
                      item.label !== removed.label ||
                      item.value !== removed.value,
                  );
                  onChange(updated);
                } else {
                  // Handle normal selection
                  onChange(selected || []);
                }
              } else {
                if (actionMeta.action === "create-option") {
                  onChange({
                    target: { name, value: { ...selected, value: "" } }, // ✅ value empty
                  });
                } else {
                  onChange({
                    target: { name, value: selected || null },
                  });
                }
              }
            }}
          />
        )}

        {inputType === "radio" && radioOptions?.length > 0 && (
          <div>
            {radioOptions.map((opt) => (
              <Form.Check
                inline
                type="radio"
                key={opt.value}
                label={opt.label}
                name={name}
                id={`${name}_${opt.value}`}
                value={opt.value}
                checked={value === opt.value}
                onChange={onChange}
                disabled={disabled}
                className="me-3"
              />
            ))}
          </div>
        )}

        {inputType === "checkbox" && (
          <div className="form-check d-flex align-items-center gap-2">
            <input
              type="checkbox"
              id={id || name}
              name={name}
              className={`form-check-input ${styles.checkbox24} body-text`}
              checked={!!value}
              onChange={(e) =>
                onChange({ target: { name, value: e.target.checked } })
              }
              disabled={disabled}
            />
            <label
              className={`form-check-label mb-0 ${
                disabled ? styles["disabled-label"] : "body-highlight"
              } ${styles.pointer}`}
              htmlFor={id || name}
            >
              {label}
            </label>
          </div>
        )}

        {inputType === "file" && (
          <>
            <Form.Control
              type="file"
              name={name}
              id={id}
              onChange={onChange}
              disabled={disabled}
              style={disabled ? disabledStyles : {}}
            />
            {placeholder && (
              <Form.Text className="text-muted" style={{ fontSize: "0.8rem" }}>
                {placeholder}
              </Form.Text>
            )}
          </>
        )}

        {inputType === "dragfile" && (
          <Dropzone
            onDrop={(acceptedFiles) => onChange(acceptedFiles)}
            multiple
            disabled={disabled}
            accept={allowedTypes} // ✅ dynamic accept from props
            // maxSize={maxFileSize}
          >
            {({ getRootProps, getInputProps }) => (
              <div
                {...getRootProps()}
                className={`${styles.dropzone} ${
                  disabled ? styles.disabled : ""
                }`}
              >
                <input {...getInputProps()} name={name} multiple />

                <BsFileEarmarkArrowUp
                  size={40}
                  className="mb-2"
                  color="var(--color-grey)"
                />

                <p className="mb-1 fw-medium text-dark">
                  Drag and Drop files here
                </p>

                {/* ✅ Show supported types dynamically */}
                <p className="mb-2 text-muted" style={{ fontSize: "0.85rem" }}>
                  Files supported:{" "}
                  {allowedTypes
                    ? Object.values(allowedTypes)
                        .flat()
                        .map((t) => t.split("/")[1] || t) // just show extension
                        .join(", ")
                    : "Any"}
                </p>

                <button
                  type="button"
                  className="btn btn-secondary btn-sm mb-2"
                  style={{ pointerEvents: "none" }}
                >
                  Choose File
                </button>

                <p className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Maximum size: {maxFileSize / (1024 * 1024)}MB
                </p>

                {Array.isArray(value) &&
                  value.map((file, idx) => (
                    <p
                      key={idx}
                      className="mt-2 text-success body-text"
                      style={{ fontSize: "0.85rem" }}
                    >
                      Selected: {file.name}
                    </p>
                  ))}
              </div>
            )}
          </Dropzone>
        )}

        {inputType === "password" ? (
          <div style={{ position: "relative" }}>
            <Form.Control
              type={showPassword ? "text" : "password"}
              name={name}
              id={id}
              ref={inputRef}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              disabled={disabled}
              style={disabled ? disabledStyles : {}}
              className={`${styles.inputBlue} body-text`} // ✅ for placeholder color
            />
            <span
              key={showPassword ? "show" : "hide"}
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#142454",
              }}
            >
              {showPassword ? <FaEye /> : <FaEyeSlash />}
            </span>
          </div>
        ) : (
          ["text", "textarea", "text area", "number", "email"].includes(
            inputType,
          ) && (
            <>
              <Form.Control
                as={
                  inputType === "textarea" || inputType === "text area"
                    ? "textarea"
                    : "input"
                }
                ref={inputRef}
                type={inputType}
                name={name}
                id={id}
                value={value}
                // onChange={(e) => {
                //   if (
                //     ["text", "textarea", "text area", "email"].includes(
                //       inputType
                //     )
                //   ) {
                //     handleTextChange(e); // ✅ use regex cleanup
                //   } else if (inputType === "number") {
                //     const newValue = e.target.value;
                //     const maxLen = maxLength || 15;
                //     if (/^\d*$/.test(newValue) && newValue.length <= maxLen) {
                //       onChange(e);
                //     }
                //   } else {
                //     onChange(e);
                //   }
                // }}
                onChange={(e) => {
                  if (
                    ["text", "textarea", "text area", "email"].includes(
                      inputType,
                    )
                  ) {
                    handleTextChange(e); // ✅ use regex cleanup
                  } else if (inputType === "number") {
                    const newValue = e.target.value;
                    const maxLen = maxLength || 15;
                    if (/^\d*$/.test(newValue) && newValue.length <= maxLen) {
                      onChange(e);
                    }
                  } else {
                    onChange(e);
                  }
                }}
                placeholder={placeholder}
                disabled={disabled}
                inputMode={inputType === "number" ? "numeric" : undefined}
                pattern={inputType === "number" ? "\\d*" : undefined}
                onWheel={(e) => inputType === "number" && e.target.blur()}
                maxLength={
                  showWordCount &&
                  (inputType === "textarea" || inputType === "text area")
                    ? 1500
                    : undefined
                }
                style={{
                  ...(inputType === "textarea" || inputType === "text area"
                    ? { height: "100px" }
                    : {}),
                  ...(disabled ? disabledStyles : {}),
                }}
                className={styles.inputBlue} // ✅ for blue placeholder
              />

              {showWordCount &&
                (inputType === "textarea" || inputType === "text area") && (
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    {charCount}/1500
                  </div>
                )}
            </>
          )
        )}
        {showErrorMessage && (
          <span
            ref={ref}
            className={`${styles.errorMessage} ${
              error ? styles.errorVisible : ""
            }`}
          >
            {error || "Invalid input"}
          </span>
        )}
      </Form.Group>
    </>
  );
}

export default Inputs;
